/**
 * Achievement Service - Sistema de logros y badges
 *
 * Diseñado para neurodivergencia:
 * - Celebra el progreso, no castiga los fallos
 * - Logros alcanzables y significativos
 * - Sin presion ni comparaciones sociales
 * - Enfocado en consistencia, no perfeccion
 *
 * Basado en: "Gamificacion optimizada basada en el historial del usuario"
 */

export type AchievementCategory =
  | 'streak'        // Rachas de consistencia
  | 'completion'    // Tareas completadas
  | 'exploration'   // Uso de features
  | 'resilience'    // Recuperarse de dias dificiles
  | 'milestone';    // Hitos importantes

export type AchievementTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  category: AchievementCategory;
  tier: AchievementTier;
  icon: string;
  /** Condicion para desbloquear */
  requirement: {
    type: 'streak' | 'total_completions' | 'perfect_days' | 'hard_days_recovered' | 'feature_used';
    value: number;
    featureId?: string;
  };
  /** Mensaje de celebracion al desbloquear */
  celebrationMessage: string;
  /** Mensaje motivacional (no presiona) */
  encouragement: string;
}

/**
 * Definicion de todos los logros disponibles
 */
export const ACHIEVEMENTS: Achievement[] = [
  // === RACHAS ===
  {
    id: 'streak-3',
    title: 'Primeros pasos',
    description: '3 dias seguidos completando protocolos',
    category: 'streak',
    tier: 'bronze',
    icon: '🌱',
    requirement: { type: 'streak', value: 3 },
    celebrationMessage: 'Has comenzado a construir un habito. Cada dia cuenta.',
    encouragement: 'La consistencia se construye paso a paso.',
  },
  {
    id: 'streak-7',
    title: 'Una semana fuerte',
    description: '7 dias seguidos de consistencia',
    category: 'streak',
    tier: 'silver',
    icon: '🌿',
    requirement: { type: 'streak', value: 7 },
    celebrationMessage: 'Una semana completa. Tu cerebro esta formando nuevas conexiones.',
    encouragement: 'Los habitos se fortalecen con cada repeticion.',
  },
  {
    id: 'streak-14',
    title: 'Dos semanas solidas',
    description: '14 dias de racha ininterrumpida',
    category: 'streak',
    tier: 'gold',
    icon: '🌳',
    requirement: { type: 'streak', value: 14 },
    celebrationMessage: 'Dos semanas. Esto ya es parte de tu rutina.',
    encouragement: 'La ciencia dice que 14 dias empiezan a formar habitos.',
  },
  {
    id: 'streak-30',
    title: 'Un mes imparable',
    description: '30 dias consecutivos',
    category: 'streak',
    tier: 'platinum',
    icon: '🏔️',
    requirement: { type: 'streak', value: 30 },
    celebrationMessage: 'Un mes entero. Has demostrado que puedes mantener el rumbo.',
    encouragement: 'Este nivel de consistencia es extraordinario.',
  },

  // === COMPLETADOS ===
  {
    id: 'complete-10',
    title: 'Empezando el viaje',
    description: 'Completar 10 protocolos en total',
    category: 'completion',
    tier: 'bronze',
    icon: '✨',
    requirement: { type: 'total_completions', value: 10 },
    celebrationMessage: 'Ya van 10. Cada uno es un paso adelante.',
    encouragement: 'No importa la velocidad, importa la direccion.',
  },
  {
    id: 'complete-50',
    title: 'Construyendo momento',
    description: '50 protocolos completados',
    category: 'completion',
    tier: 'silver',
    icon: '⭐',
    requirement: { type: 'total_completions', value: 50 },
    celebrationMessage: '50 protocolos. Estas creando un patron de exito.',
    encouragement: 'El progreso acumulado es poderoso.',
  },
  {
    id: 'complete-100',
    title: 'Centurion',
    description: '100 protocolos en tu historial',
    category: 'completion',
    tier: 'gold',
    icon: '🌟',
    requirement: { type: 'total_completions', value: 100 },
    celebrationMessage: 'Has llegado a 100. Eso es dedicacion real.',
    encouragement: 'Cien veces elegiste cuidarte.',
  },
  {
    id: 'complete-500',
    title: 'Maestro del habito',
    description: '500 protocolos completados',
    category: 'completion',
    tier: 'platinum',
    icon: '💫',
    requirement: { type: 'total_completions', value: 500 },
    celebrationMessage: 'Quinientos. Has hecho de esto un estilo de vida.',
    encouragement: 'Eres un ejemplo de perseverancia.',
  },

  // === DIAS PERFECTOS ===
  {
    id: 'perfect-1',
    title: 'Dia redondo',
    description: 'Completar todos los protocolos de un dia',
    category: 'completion',
    tier: 'bronze',
    icon: '🎯',
    requirement: { type: 'perfect_days', value: 1 },
    celebrationMessage: 'Un dia perfecto. Saborea este momento.',
    encouragement: 'No necesitas dias perfectos, pero celebralos cuando lleguen.',
  },
  {
    id: 'perfect-5',
    title: 'Cinco estrellas',
    description: '5 dias perfectos en total',
    category: 'completion',
    tier: 'silver',
    icon: '🏆',
    requirement: { type: 'perfect_days', value: 5 },
    celebrationMessage: 'Cinco dias al 100%. Impresionante.',
    encouragement: 'Los dias perfectos no definen tu valor, pero muestran tu capacidad.',
  },
  {
    id: 'perfect-20',
    title: 'Coleccionista de dias',
    description: '20 dias perfectos acumulados',
    category: 'completion',
    tier: 'gold',
    icon: '👑',
    requirement: { type: 'perfect_days', value: 20 },
    celebrationMessage: 'Veinte dias perfectos. Has encontrado tu ritmo.',
    encouragement: 'Sabes lo que funciona para ti.',
  },

  // === RESILIENCIA ===
  {
    id: 'resilience-1',
    title: 'Rebote',
    description: 'Completar protocolos despues de un dia dificil',
    category: 'resilience',
    tier: 'bronze',
    icon: '🔄',
    requirement: { type: 'hard_days_recovered', value: 1 },
    celebrationMessage: 'Volviste. Eso es lo que importa.',
    encouragement: 'Caer es humano. Levantarse es fortaleza.',
  },
  {
    id: 'resilience-5',
    title: 'Inquebrantable',
    description: 'Recuperarte 5 veces de dias dificiles',
    category: 'resilience',
    tier: 'silver',
    icon: '💪',
    requirement: { type: 'hard_days_recovered', value: 5 },
    celebrationMessage: 'Cinco veces te has levantado. Eres mas fuerte de lo que crees.',
    encouragement: 'La resiliencia es tu superpoder.',
  },
  {
    id: 'resilience-10',
    title: 'Fenix',
    description: '10 recuperaciones de dias dificiles',
    category: 'resilience',
    tier: 'gold',
    icon: '🔥',
    requirement: { type: 'hard_days_recovered', value: 10 },
    celebrationMessage: 'Como el fenix, siempre te levantas. Admirable.',
    encouragement: 'Has demostrado que ningun mal dia te define.',
  },

  // === EXPLORACION ===
  {
    id: 'explore-timer',
    title: 'Gestor del tiempo',
    description: 'Usar el timer Pomodoro',
    category: 'exploration',
    tier: 'bronze',
    icon: '⏱️',
    requirement: { type: 'feature_used', value: 1, featureId: 'timer' },
    celebrationMessage: 'Descubriste el timer. El tiempo es tu aliado.',
    encouragement: 'Las sesiones cortas pueden hacer grandes diferencias.',
  },
  {
    id: 'explore-focus',
    title: 'Modo zen',
    description: 'Usar el modo focus',
    category: 'exploration',
    tier: 'bronze',
    icon: '🧘',
    requirement: { type: 'feature_used', value: 1, featureId: 'focus' },
    celebrationMessage: 'El modo focus elimina distracciones. Bien hecho.',
    encouragement: 'Una cosa a la vez es la clave.',
  },
  {
    id: 'explore-export',
    title: 'Dueno de tus datos',
    description: 'Exportar tus datos',
    category: 'exploration',
    tier: 'bronze',
    icon: '📦',
    requirement: { type: 'feature_used', value: 1, featureId: 'export' },
    celebrationMessage: 'Tus datos son tuyos. Siempre.',
    encouragement: 'El control de tus datos es importante.',
  },
];

/**
 * Estado de logros del usuario
 */
export interface UserAchievementState {
  unlockedIds: string[];
  lastUnlocked: string | null;
  stats: {
    currentStreak: number;
    totalCompletions: number;
    perfectDays: number;
    hardDaysRecovered: number;
    featuresUsed: string[];
  };
}

const STORAGE_KEY = 'exocortex-achievements';

/**
 * Carga el estado de logros del usuario
 */
export function loadAchievementState(): UserAchievementState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as UserAchievementState;
    }
  } catch (error) {
    console.error('Error loading achievements:', error);
  }

  return {
    unlockedIds: [],
    lastUnlocked: null,
    stats: {
      currentStreak: 0,
      totalCompletions: 0,
      perfectDays: 0,
      hardDaysRecovered: 0,
      featuresUsed: [],
    },
  };
}

/**
 * Guarda el estado de logros
 */
export function saveAchievementState(state: UserAchievementState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving achievements:', error);
  }
}

/**
 * Verifica si un logro esta desbloqueado
 */
export function isAchievementUnlocked(
  achievement: Achievement,
  state: UserAchievementState
): boolean {
  return state.unlockedIds.includes(achievement.id);
}

/**
 * Verifica si un logro puede ser desbloqueado con el estado actual
 */
export function canUnlockAchievement(
  achievement: Achievement,
  state: UserAchievementState
): boolean {
  if (isAchievementUnlocked(achievement, state)) return false;

  const { requirement } = achievement;
  const { stats } = state;

  switch (requirement.type) {
    case 'streak':
      return stats.currentStreak >= requirement.value;
    case 'total_completions':
      return stats.totalCompletions >= requirement.value;
    case 'perfect_days':
      return stats.perfectDays >= requirement.value;
    case 'hard_days_recovered':
      return stats.hardDaysRecovered >= requirement.value;
    case 'feature_used':
      return requirement.featureId
        ? stats.featuresUsed.includes(requirement.featureId)
        : false;
    default:
      return false;
  }
}

/**
 * Intenta desbloquear nuevos logros y devuelve los desbloqueados
 */
export function checkAndUnlockAchievements(
  state: UserAchievementState
): Achievement[] {
  const newlyUnlocked: Achievement[] = [];

  for (const achievement of ACHIEVEMENTS) {
    if (canUnlockAchievement(achievement, state)) {
      state.unlockedIds.push(achievement.id);
      state.lastUnlocked = achievement.id;
      newlyUnlocked.push(achievement);
    }
  }

  if (newlyUnlocked.length > 0) {
    saveAchievementState(state);
  }

  return newlyUnlocked;
}

/**
 * Actualiza stats y verifica logros
 */
export function updateStats(
  updates: Partial<UserAchievementState['stats']>
): Achievement[] {
  const state = loadAchievementState();

  // Actualizar stats
  state.stats = {
    ...state.stats,
    ...updates,
  };

  saveAchievementState(state);

  // Verificar nuevos logros
  return checkAndUnlockAchievements(state);
}

/**
 * Registra el uso de una feature
 */
export function trackFeatureUsed(featureId: string): Achievement[] {
  const state = loadAchievementState();

  if (!state.stats.featuresUsed.includes(featureId)) {
    state.stats.featuresUsed.push(featureId);
    saveAchievementState(state);
  }

  return checkAndUnlockAchievements(state);
}

/**
 * Obtiene logros por categoria
 */
export function getAchievementsByCategory(
  category: AchievementCategory
): Achievement[] {
  return ACHIEVEMENTS.filter((a) => a.category === category);
}

/**
 * Obtiene el progreso hacia un logro (0-100)
 */
export function getAchievementProgress(
  achievement: Achievement,
  state: UserAchievementState
): number {
  if (isAchievementUnlocked(achievement, state)) return 100;

  const { requirement } = achievement;
  const { stats } = state;

  let current = 0;
  switch (requirement.type) {
    case 'streak':
      current = stats.currentStreak;
      break;
    case 'total_completions':
      current = stats.totalCompletions;
      break;
    case 'perfect_days':
      current = stats.perfectDays;
      break;
    case 'hard_days_recovered':
      current = stats.hardDaysRecovered;
      break;
    case 'feature_used':
      current = requirement.featureId && stats.featuresUsed.includes(requirement.featureId) ? 1 : 0;
      break;
  }

  return Math.min(100, Math.round((current / requirement.value) * 100));
}
